import { ChangeEvent, FC, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  NumberInput,
  NumberInputField,
  Select,
  Text,
} from '@chakra-ui/react';
import { LuChevronDown, LuChevronUp, LuTrash2 } from 'react-icons/lu';
import type { AnimationEffect } from '@adflow/types';
import theme from '@adflow/theme';
import { NAMED_EASINGS, isNamedEasing, type NamedEasing } from '@adflow/utils';
import { getAnimationPreset, resolvePresetParams } from '../../../animation';
import { ANIMATION_ROLE_LABEL, listPresetsForRole } from './presetDisplay';

const EASING_LABELS: Record<NamedEasing, string> = {
  linear: 'Linear',
  'ease-in': 'Ease In',
  'ease-out': 'Ease Out',
  'ease-in-out': 'Ease In Out',
};

const fieldLabelStyles = {
  fontSize: 'xs',
  fontWeight: 'medium',
  color: 'gray.500',
  mb: 1,
};

type NumberParamFieldProps = {
  label: string;
  value: number;
  min: number;
  step: number;
  precision: number;
  testId: string;
  onCommit: (value: number) => void;
};

const NumberParamField: FC<NumberParamFieldProps> = ({
  label,
  value,
  min,
  step,
  precision,
  testId,
  onCommit,
}) => {
  // Keep the raw text while the field is being edited so transient states like
  // "" or "0." are not snapped back to the committed value on every keystroke.
  // Only finite, in-range values are committed.
  const [draft, setDraft] = useState<string | null>(null);

  const handleChange = (valueAsString: string, valueAsNumber: number) => {
    setDraft(valueAsString);
    if (Number.isFinite(valueAsNumber) && valueAsNumber >= min && valueAsNumber !== value) {
      onCommit(valueAsNumber);
    }
  };

  return (
    <FormControl>
      <FormLabel {...fieldLabelStyles}>{label}</FormLabel>
      <NumberInput
        size="sm"
        min={min}
        step={step}
        precision={precision}
        clampValueOnBlur={false}
        value={draft ?? value}
        onChange={handleChange}
      >
        <NumberInputField data-test-id={testId} onBlur={() => setDraft(null)} />
      </NumberInput>
    </FormControl>
  );
};

type Props = {
  effect: AnimationEffect;
  isOpen: boolean;
  blockedPresetIds: ReadonlySet<string>;
  onToggle: () => void;
  onChangePreset: (effectId: string, presetId: string) => void;
  onUpdate: (effectId: string, params: Record<string, number | string | boolean>) => void;
  onRemove: (effectId: string) => void;
};

export const AppliedEffectEditor: FC<Props> = ({
  effect,
  isOpen,
  blockedPresetIds,
  onToggle,
  onChangePreset,
  onUpdate,
  onRemove,
}) => {
  const preset = getAnimationPreset(effect.presetId);
  const params = resolvePresetParams(preset, effect.params);
  const isSlide = preset.group === 'slide';
  const rolePresets = listPresetsForRole(preset.phase);
  const panelId = `applied-effect-panel-${effect.id}`;

  const handleNumberParam = (key: 'duration' | 'start' | 'distance') => (value: number) => {
    onUpdate(effect.id, { [key]: value });
  };

  const handleEasingChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (isNamedEasing(event.target.value)) {
      onUpdate(effect.id, { easing: event.target.value });
    }
  };

  const handlePresetChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const presetId = event.target.value;
    if (presetId === effect.presetId || blockedPresetIds.has(presetId)) {
      return;
    }
    onChangePreset(effect.id, presetId);
  };

  return (
    <Flex
      direction="column"
      gap={3}
      p={4}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      data-test-id={`applied-effect-${effect.id}`}
      data-applied-preset={effect.presetId}
    >
      <Flex alignItems="center" justifyContent="space-between" gap={2}>
        <Text fontSize="sm" fontWeight="bold" color={theme.palette.mcNavy.main} noOfLines={1}>
          {preset.label}
        </Text>
        <Flex alignItems="center" gap={2} flexShrink={0}>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={theme.palette.mcGreen.main}
            data-test-id={`applied-effect-role-${effect.id}`}
          >
            {ANIMATION_ROLE_LABEL[preset.phase]}
          </Text>
          <IconButton
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${preset.label}`}
            aria-expanded={isOpen}
            aria-controls={panelId}
            icon={<Icon as={isOpen ? LuChevronUp : LuChevronDown} boxSize={4} />}
            size="sm"
            variant="ghost"
            color="gray.600"
            onClick={onToggle}
            data-test-id={`applied-effect-toggle-${effect.id}`}
          />
        </Flex>
      </Flex>
      {isOpen ? (
        <Flex id={panelId} direction="column" gap={3}>
          <FormControl>
            <FormLabel {...fieldLabelStyles}>Type</FormLabel>
            <Select
              size="sm"
              value={effect.presetId}
              onChange={handlePresetChange}
              data-test-id={`applied-effect-type-${effect.id}`}
            >
              {rolePresets.map((rolePreset) => (
                <option
                  key={rolePreset.id}
                  value={rolePreset.id}
                  disabled={blockedPresetIds.has(rolePreset.id)}
                >
                  {rolePreset.label}
                </option>
              ))}
            </Select>
          </FormControl>
          <NumberParamField
            label="Duration (s)"
            value={params.duration}
            min={0}
            step={0.1}
            precision={2}
            testId={`applied-effect-duration-${effect.id}`}
            onCommit={handleNumberParam('duration')}
          />
          <FormControl>
            <FormLabel {...fieldLabelStyles}>Easing</FormLabel>
            <Select
              size="sm"
              value={params.easing}
              onChange={handleEasingChange}
              data-test-id={`applied-effect-easing-${effect.id}`}
            >
              {(Object.keys(NAMED_EASINGS) as NamedEasing[]).map((easing) => (
                <option key={easing} value={easing}>
                  {EASING_LABELS[easing]}
                </option>
              ))}
            </Select>
          </FormControl>
          <NumberParamField
            label="Start (s)"
            value={params.start}
            min={0}
            step={0.1}
            precision={2}
            testId={`applied-effect-start-${effect.id}`}
            onCommit={handleNumberParam('start')}
          />
          {isSlide && (
            <NumberParamField
              label="Distance (px)"
              value={params.distance ?? 0}
              min={0}
              step={1}
              precision={0}
              testId={`applied-effect-distance-${effect.id}`}
              onCommit={handleNumberParam('distance')}
            />
          )}
          <Flex justifyContent="flex-end">
            <Button
              size="sm"
              variant="ghost"
              color="gray.600"
              leftIcon={<LuTrash2 />}
              onClick={() => onRemove(effect.id)}
              data-test-id={`applied-effect-remove-${effect.id}`}
            >
              Remove
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
};
